import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical, PencilLine } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { EditFieldCard, EditFieldPad, EditFieldRow, EditFieldRowLeft, EditFieldRowValue } from "./EditField";
import { Collapse } from "./Collapse";

// Expanded description: pencil + auto-sizing transparent textarea.
const DescriptionRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": {
    flexShrink: 0,
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
    marginTop: theme.fieldSalesTwo.spacing[2],
  },
}));

const DescriptionInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: "5.5rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

const RowValueText = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

interface TodoDescriptionFieldProps {
  description: string;
  /** When true the description collapses into a one-line summary row (the picker is open). */
  collapsed: boolean;
  onChange: (value: string) => void;
  /** Expand the description (closes the date picker). */
  onExpand: () => void;
}

/** Description section of the recap edit-todo screen: editable when open, a one-line row when collapsed. */
export function TodoDescriptionField({ description, collapsed, onChange, onExpand }: TodoDescriptionFieldProps) {
  const t = useTranslations("fieldSalesTwo.recaps");

  return (
    <EditFieldCard>
      <Collapse open={!collapsed}>
        <EditFieldPad>
          <DescriptionRow>
            <PencilLine aria-hidden="true" />
            <DescriptionInput
              value={description}
              onChange={(e) => onChange(e.target.value)}
              placeholder={t("edit.description")}
              aria-label={t("edit.description")}
            />
          </DescriptionRow>
        </EditFieldPad>
      </Collapse>
      <Collapse open={collapsed}>
        <EditFieldRow type="button" onClick={onExpand}>
          <EditFieldRowLeft>
            <PencilLine aria-hidden="true" />
            {t("edit.description")}
          </EditFieldRowLeft>
          <EditFieldRowValue>
            <RowValueText>{description}</RowValueText>
            <ChevronGrabberVertical aria-hidden="true" />
          </EditFieldRowValue>
        </EditFieldRow>
      </Collapse>
    </EditFieldCard>
  );
}
